import { useMemo, useState, type VoidFunctionComponent } from 'react';
import { useSelector } from 'react-redux';

import { plural } from '../../../../shared/helpers';
import { useParams } from '../../../contexts/RouterContext';
import { useDebouncedInputValue } from '../../../hooks';
import useVendoredDependencies from '../../../hooks/api/dependencies/useVendoredDependencies';
import * as DependenciesV2Store from '../../../redux/dependenciesV2';
import { convertSearchByLocatorQuery } from '../../../redux/dependenciesV2/actions';
import { SearchInput } from '../../generic';
import SubnavCountBadge from '../../generic/Subnav/SubnavCountBadge';
import { useDependencyDrawer } from '../dependenciesV2/hooks/useDependencyDrawer';
import { useSetDependencyScope } from '../dependenciesV2/hooks/useSetDependenciesScope';
import { TreeViewLayout } from '../shared/TreeViewLayout';
import type { RevisionRouteParams } from '../projectDashboardTypes';

import VendoredDependenciesTable from './Table';
import VendoredDependenciesTreeView from './TreeView';
import { useSelectedPath } from './useSelectedPath';
import { summarizePath } from './utils';

import styles from './VendoredDependencies.module.less';

/**
 * Root component for the Vendored Dependencies page.
 * Includes only the content of the page, the SidebarNavLayout is handled in the parent route.
 */
const VendoredDependenciesContainer: VoidFunctionComponent = () => {
  const params = useParams<RevisionRouteParams>();
  const userRevisionLocator = `${params.project_id}$${params.revision_id}`;
  const scope = useSelector(DependenciesV2Store.selectors.getScope);

  const [selectedDependencyRevisionLocator, setSelectedDependencyRevisionLocator] = useState<string | null>(null);

  // Track which path is currently selected
  const { selectedPath, setSelectedPath } = useSelectedPath();
  //  Note that the user is allowed to filter by locator or title.
  const { value, debouncedValue, onChange } = useDebouncedInputValue();

  //  This method will convert the `title` query param to a `locators` query param if it seems to be a locator.
  //  This is so that users can search by locator or title.
  const getDepsQuery = useMemo(() => convertSearchByLocatorQuery({
    title: debouncedValue?.trim() || undefined,
  }), [debouncedValue]);

  //  Fetch the vendored dependencies and the total count.
  //  The total count is used in a few different places higher up the component tree,
  //  which is why we don't fetch it down in the VendoredDependenciesTable component.
  const { data, isLoading, error } = useVendoredDependencies(userRevisionLocator, selectedPath, getDepsQuery);
  const dependencies = data?.dependencies ?? [];
  const totalCount = data?.total ?? 0;

  useDependencyDrawer({
    scope,
    selectedDependencyRevisionLocator,
    setSelectedDependencyRevisionLocator,
  });

  useSetDependencyScope({
    projectId: params.project_id,
    revisionId: params.revision_id,
  });

  return (
    <>
      <header className={styles.header}>
        <h1 className={styles.title}>
          Vendored Dependencies
        </h1>
        <span className={styles.summary}>
          {totalCount} vendored {plural(data?.total ?? 0, 'dependency', 'dependencies')} found in <span className="bold">{summarizePath(selectedPath ?? '/')}</span>
        </span>
      </header>
      <div>
        <span className={styles.search}>
          <SearchInput
            label="vendored-dependencies-filter-search"
            placeholder="Filter by name or locator"
            size="small"
            value={value}
            onChange={onChange}
          />
        </span>
        {/* TODO: later if we add more filters or the compare button, it will go here */}
      </div>
      <TreeViewLayout>
        <VendoredDependenciesTreeView
          userRevisionLocator={userRevisionLocator}
          selectedPath={selectedPath}
          setSelectedPath={setSelectedPath}
        />
        {/* TODO: this will be extracted into a component later */}
        <TreeViewLayout.Table
          header={(
            <span className={styles.tableHeader}>
              Components <SubnavCountBadge count={totalCount} isLoading={isLoading} />
            </span>
          )}
        >
          <VendoredDependenciesTable
            isLoading={isLoading}
            error={error}
            dependencies={dependencies}
            setSelectedDependencyRevisionLocator={setSelectedDependencyRevisionLocator}
          />
        </TreeViewLayout.Table>
      </TreeViewLayout>
    </>
  );
};

export default VendoredDependenciesContainer;
